import $axios from '~/plugins/axios';

import { Api, GetSettingTagParams, Tag } from '~/api';
import { buildModule } from '../utils';
import { createDict, Dict, fetchAllPages } from '~/helpers';

const api = $axios as Api;

type LazyLoadingParams = Omit<GetSettingTagParams, 'limit' | 'offset'>;

export default buildModule()
	.withState({
		tagById: {} as Dict<Tag>,
		tagByName: {} as Dict<Tag>,
		orderedIds: [] as Tag['id'][],
		params: {} as LazyLoadingParams,
	})
	.withGetters({
		items: (state) => state.orderedIds.map(id => state.tagById[id]),
		byId: (state) => (id: Tag['id']): Tag | null => state.tagById[id] ?? null,
		byName: (state) => (name: Tag['name']): Tag | null => state.tagByName[name] ?? null,
	})
	.withMutations({
		RESET (state, params: LazyLoadingParams) {
			state.params = params;
			state.orderedIds = [];
		},
		APPEND_TAGS (state, tags: Tag[]) {
			state.tagById = { ...state.tagById, ...createDict(tags, ({ id }) => id) };
			state.tagByName = { ...state.tagByName, ...createDict(tags, ({ name }) => name) };
			state.orderedIds = Array.from(new Set([ ...state.orderedIds, ...tags.map((tag) => tag.id) ]));
		},
		SET_TAGS (state, tags: Tag[]) {
			state.tagById = { ...state.tagById, ...createDict(tags, ({ id }) => id) };
			state.tagByName = { ...state.tagByName, ...createDict(tags, ({ name }) => name) };
		},
	})
	.withActions({
		async getMore ({ state, commit }, limit: number | null = 20) {
			const params: GetSettingTagParams = { ...state.params, limit, offset: state.orderedIds.length };
			const { data } = await api.get('/setting/tag', { params });

			commit('APPEND_TAGS', data.payload);
		},
		async fetchInitialTags ({ commit }, tagIds: Tag['id'][]) {
			const tagPromises = tagIds.map((id) => api.get(`/setting/tag/${ id }`));
			const tagResponses = await Promise.all(tagPromises);

			const tags = tagResponses.map((resp) => resp.data.payload);

			commit('SET_TAGS', tags);
		},
		async getAll ({ state, commit }, params: LazyLoadingParams) {
			const allParams: GetSettingTagParams = { ...state.params, ...params };
			const items = await fetchAllPages('/setting/tag', allParams)<Tag>(api);
			commit('SET_TAGS', items);

			return items;
		},
		resetParams ({ state, commit }, params: LazyLoadingParams) {
			commit('RESET', { ...state.params, ...params });
		},
	});
